import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import Logo from './Logo';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'already' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [website, setWebsite] = useState(''); // honeypot

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !turnstileToken) return;

    setStatus('loading');
    try {
      const response = await fetch('/api/newsletter/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          turnstile_token: turnstileToken,
          website,
        }),
      });
      if (response.ok) {
        setStatus('success');
        setEmail('');
      } else {
        const data = await response.json().catch(() => ({}));
        if (data.detail?.includes('already subscribed')) {
          setStatus('already');
          setEmail('');
        } else {
          const detail =
            (Array.isArray(data?.email) && data.email[0]) ||
            data?.detail ||
            'Something went wrong. Please try again.';
          setErrorMsg(detail);
          setStatus('error');
        }
      }
    } catch {
      setErrorMsg('Network error. Please try again.');
      setStatus('error');
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-slate-400 mt-auto">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand & Newsletter */}
          <div className="lg:col-span-2">
            <div className="mb-4">
              <Logo variant="light" size="lg" />
            </div>
            <p className="text-sm mb-6 max-w-md">
              Your source for technology, gaming, business, and lifestyle content.
            </p>

            {/* Newsletter */}
            <form onSubmit={handleSubscribe} className="max-w-sm">
              <div className="flex gap-2">
                <label htmlFor="footer-newsletter-email" className="sr-only">Email address</label>
                <input
                  id="footer-newsletter-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={status === 'loading' || status === 'success'}
                />
                <button
                  type="submit"
                  disabled={status === 'loading' || status === 'success' || !turnstileToken}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? '...' : status === 'success' ? 'Done' : 'Subscribe'}
                </button>
              </div>
              {/* Honeypot — bots fill this; humans never see it. */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
              />
              {TURNSTILE_SITE_KEY && (
                <div className="mt-3">
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onExpire={() => setTurnstileToken('')}
                    onError={() => setTurnstileToken('')}
                    options={{ size: 'flexible', appearance: 'interaction-only', theme: 'dark' }}
                  />
                </div>
              )}
            </form>
            {status === 'success' && (
              <p className="text-green-400 text-xs mt-2">Please check your email to confirm your subscription.</p>
            )}
            {status === 'already' && (
              <p className="text-green-400 text-xs mt-2">You're already subscribed — you're all set. ✓</p>
            )}
            {status === 'error' && (
              <p className="text-red-400 text-xs mt-2">{errorMsg || 'Something went wrong. Please try again.'}</p>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-medium mb-4 text-sm">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/news/" className="hover:text-white transition-colors">News</Link></li>
              <li><Link to="/categories" className="hover:text-white transition-colors">Categories</Link></li>
              <li><Link to="/tools/" className="hover:text-white transition-colors">Free Tools</Link></li>
              <li><Link to="/compare" className="hover:text-white transition-colors">Compare</Link></li>
              <li><Link to="/guides" className="hover:text-white transition-colors">Guides</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
              <li><Link to="/editorial-standards" className="hover:text-white transition-colors">Editorial Standards</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              {/* The commercial doors. Both pages existed and converted; neither was
                  linked from the footer, which is where a B2B visitor looks for them. */}
              <li><Link to="/advertise/" className="hover:text-white transition-colors">Advertise</Link></li>
              <li><Link to="/publish-article/" className="hover:text-white transition-colors">Sponsored Posts</Link></li>
            </ul>
          </div>

          {/* Popular Tools */}
          <div>
            <h3 className="text-white font-medium mb-4 text-sm">Popular Tools</h3>
            <ul className="space-y-2 text-sm">
              {/* Reworked 2026-09-11. Dropped Grammar Checker, AI Writing, AI Motion
                  Control and Speed Test; promoted the generators and calculators.
                  Those four pages are all still live and routed — they are simply no
                  longer surfaced here. Trailing slashes below match each page's own
                  canonical, so these do not take the extra 301 hop. */}
              <li><Link to="/free-invoice-generator/" className="hover:text-white transition-colors">Invoice Generator</Link></li>
              <li><Link to="/free-salary-slip-generator/" className="hover:text-white transition-colors">Salary Slip Generator</Link></li>
              <li><Link to="/free-gst-calculator/" className="hover:text-white transition-colors">GST Calculator</Link></li>
              <li><Link to="/perfect-weight-calculator/" className="hover:text-white transition-colors">Perfect Weight Calculator</Link></li>
              <li><Link to="/calorie-calculator/" className="hover:text-white transition-colors">Calorie Calculator</Link></li>
              <li><Link to="/free-image-compressor/" className="hover:text-white transition-colors">Image Compressor</Link></li>
              <li><Link to="/qr-code-generator/" className="hover:text-white transition-colors">QR Generator</Link></li>
              <li><Link to="/what-is-my-ip/" className="hover:text-white transition-colors">What Is My IP</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-10 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            {/* slate-400, not slate-500: on the #0f172a footer this is 6.86:1
                against 3.75:1, which failed WCAG AA (4.5:1) for 12px text. */}
            <p className="text-xs text-slate-400">
              © {currentYear} SaveDelete. All rights reserved.
            </p>

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-4 text-xs">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
              <a href="/sitemap.xml" className="hover:text-white transition-colors">Sitemap</a>
              <a href="/feed" className="hover:text-white transition-colors">RSS</a>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a href="https://x.com/save_delete" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" aria-label="X">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a href="https://facebook.com/savedelete" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" aria-label="Facebook">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://linkedin.com/company/savedelete" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" aria-label="LinkedIn">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="https://www.youtube.com/@save_delete" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" aria-label="YouTube">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a href="https://pinterest.com/savedelete" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" aria-label="Pinterest">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
